import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ClipboardCheck, Loader2, Pencil, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { useAuth } from "../context/AuthContext";
import { useRevalidateOnFocus } from "../lib/useRevalidateOnFocus";
import EditSupervisorNoteDialog from "./EditSupervisorNoteDialog";
import FlagButton from "./FlagButton";
import VisibilityPill from "./VisibilityPill";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Iter-60: Read-only list of supervisor notes for a project, optionally
 * filtered by scene_id or character_id. Owners can delete individual
 * notes. Display-only — these notes NEVER mix with department continuity
 * notes (the per-pair `notes` field on ContinuityBlock).
 */
export default function SupervisorNotesList({
    projectId,
    sceneId,
    characterId,
    isOwner = false,
    showSceneRef = true,
    showCharacterRef = true,
    emptyHint,
    testidPrefix = "supervisor-notes",
    onChange,
    // iter-80 PART 3 + 4 — client-side text search and team filter.
    // Optional; when omitted, list behaves exactly as before.
    searchQuery = "",
    teamFilter = "all",
    characterTeamMap = null,
    // iter-101 — server-side episode filter. When set, backend returns
    // only notes linked to scenes OR characters of that episode.
    episode = null,
    // Iter-131b — server-side archive + category filters.
    archived = "false", // "false" | "true" | "all"
    categories = null, // null | comma-separated category names
}) {
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    // Iter-156D — current user id is needed to determine "this is
    // MY note" for the row-level Edit / Delete gate. Backend
    // ACL is creator-or-admin; we mirror it here so we never show
    // a button that would 403.
    const { user } = useAuth();
    const myId = user?.id || null;
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    // Iter-96 PART 3 — owner can correct an existing note.
    const [editingNote, setEditingNote] = useState(null);
    const [editScenes, setEditScenes] = useState([]);
    const [editChars, setEditChars] = useState([]);
    const [archivingId, setArchivingId] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (sceneId) params.set("scene_id", sceneId);
            if (characterId) params.set("character_id", characterId);
            if (episode) params.set("episode", episode);
            if (archived && archived !== "false") params.set("archived", archived);
            if (categories) params.set("category", categories);
            const qs = params.toString();
            const { data } = await api.get(
                `${apiBase}/${projectId}/supervisor-notes${qs ? `?${qs}` : ""}`,
            );
            setNotes(data || []);
            setError("");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [projectId, sceneId, characterId, episode, archived, categories]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Iter-156E — calm cross-device refresh. When the tab regains
    // focus, re-run `refresh()` so notes added/edited/deleted by
    // collaborators on the same project appear without a manual
    // page reload. Throttled to once every 1.5s by the hook.
    useRevalidateOnFocus(refresh);

    const onDelete = async (id) => {
        setDeletingId(id);
        try {
            await api.delete(
                `${apiBase}/${projectId}/supervisor-notes/${id}`,
            );
            setNotes((prev) => prev.filter((n) => n.id !== id));
            toast.success("Note removed");
            if (onChange) onChange();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeletingId(null);
            setConfirmDeleteId(null);
        }
    };
    const requestDelete = (id) => setConfirmDeleteId(id);

    const toggleArchive = async (note) => {
        setArchivingId(note.id);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/supervisor-notes/${note.id}`,
                { archived: !note.archived },
            );
            // When toggling INSIDE the active view, an archived note
            // should fall out of the list immediately (and vice
            // versa). Drop it locally + tell the parent so the count
            // refreshes.
            const shouldRemove =
                (archived === "false" && data.archived) ||
                (archived === "true" && !data.archived);
            if (shouldRemove) {
                setNotes((prev) => prev.filter((n) => n.id !== note.id));
            } else {
                setNotes((prev) =>
                    prev.map((n) => (n.id === data.id ? data : n)),
                );
            }
            toast.success(data.archived ? "Note archived" : "Note restored");
            if (onChange) onChange();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setArchivingId(null);
        }
    };

    const startEdit = async (note) => {
        // Lazy-load scenes + characters the first time the user opens
        // an edit. Cached after that.
        if (editScenes.length === 0 || editChars.length === 0) {
            try {
                const [{ data: sc }, { data: ch }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}/scenes`),
                    api.get(`${apiBase}/${projectId}/characters`),
                ]);
                setEditScenes(sc || []);
                setEditChars(ch || []);
            } catch {
                /* dialog will still render with empty selects */
            }
        }
        setEditingNote(note);
    };

    const saveEdit = async (payload) => {
        const { data } = await api.patch(
            `${apiBase}/${projectId}/supervisor-notes/${editingNote.id}`,
            payload,
        );
        setNotes((prev) => prev.map((n) => (n.id === data.id ? data : n)));
        toast.success("Note updated");
        if (onChange) onChange();
    };

    if (loading) {
        return (
            <div
                data-testid={`${testidPrefix}-loading`}
                className="h-16 rounded-xl border border-white/10 bg-card/40 animate-pulse"
            />
        );
    }

    if (error) {
        return (
            <p
                data-testid={`${testidPrefix}-error`}
                className="text-xs text-red-300"
            >
                {error}
            </p>
        );
    }

    if (notes.length === 0) {
        if (!emptyHint) return null;
        return (
            <p
                data-testid={`${testidPrefix}-empty`}
                className="text-[11px] text-neutral-500 italic"
            >
                {emptyHint}
            </p>
        );
    }

    // iter-80 PART 3 + 4 — client-side filtering. We deliberately do
    // it post-fetch so the existing scene/character API params keep
    // working and we don't need new server endpoints for what is
    // ultimately a presentation concern.
    const needle = (searchQuery || "").trim().toLowerCase();
    const visible = notes.filter((n) => {
        if (teamFilter && teamFilter !== "all") {
            // Only apply the team gate when we actually know each
            // character's team (parent passes `characterTeamMap`).
            // Notes with no character_id are kept for "all" only;
            // they fall out as soon as a specific team is picked
            // because we have no team to compare against.
            if (!characterTeamMap || !n.character_id) return false;
            const team = characterTeamMap[n.character_id] || "main";
            if (team !== teamFilter) return false;
        }
        if (!needle) return true;
        // Search note text + scene reference (number/heading) +
        // character reference (name).
        const hay = [
            n.text,
            n.scene_number != null ? `scene ${n.scene_number}` : "",
            n.scene_number,
            n.scene_heading,
            n.character_name,
            n.label,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return hay.includes(needle);
    });

    if (visible.length === 0) {
        if (!emptyHint) return null;
        return (
            <p
                data-testid={`${testidPrefix}-empty`}
                className="text-[11px] text-neutral-500 italic"
            >
                {emptyHint}
            </p>
        );
    }

    return (
        <>
        <ul
            data-testid={`${testidPrefix}-list`}
            className="space-y-1.5"
        >
            {visible.map((n) => (
                <li
                    key={n.id}
                    data-testid={`${testidPrefix}-row-${n.id}`}
                    className="rounded-md border border-violet-500/20 bg-violet-500/5 px-3 py-2 flex items-start gap-2"
                >
                    <ClipboardCheck
                        size={13}
                        className="text-violet-300 mt-0.5 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                        <p
                            className="text-sm text-neutral-100 leading-snug whitespace-pre-wrap break-words"
                            data-testid={`${testidPrefix}-text-${n.id}`}
                        >
                            {n.text}
                        </p>
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                            {showSceneRef && n.scene_number ? (
                                n.scene_id ? (
                                    <Link
                                        to={`${routeBase}/${projectId}/scenes/${n.scene_id}`}
                                        data-testid={`${testidPrefix}-scene-ref-${n.id}`}
                                        className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5 hover:bg-violet-500/25"
                                    >
                                        Sc {n.scene_number}
                                    </Link>
                                ) : (
                                    <span
                                        data-testid={`${testidPrefix}-scene-ref-${n.id}`}
                                        className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5"
                                    >
                                        Sc {n.scene_number}
                                    </span>
                                )
                            ) : null}
                            {showCharacterRef && n.character_name ? (
                                n.character_id ? (
                                    <Link
                                        to={`${routeBase}/${projectId}/characters/${n.character_id}`}
                                        data-testid={`${testidPrefix}-char-ref-${n.id}`}
                                        className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5 hover:bg-violet-500/25"
                                    >
                                        {n.character_name}
                                    </Link>
                                ) : (
                                    <span
                                        data-testid={`${testidPrefix}-char-ref-${n.id}`}
                                        className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5"
                                    >
                                        {n.character_name}
                                    </span>
                                )
                            ) : null}
                            {n.label ? (
                                <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-400 bg-secondary border border-white/10 rounded-full px-1.5 py-0.5">
                                    {n.label}
                                </span>
                            ) : null}
                            {n.category ? (
                                <span
                                    data-testid={`${testidPrefix}-category-pill-${n.id}`}
                                    className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/10 border border-violet-500/20 rounded-full px-1.5 py-0.5"
                                >
                                    {n.category}
                                </span>
                            ) : null}
                            {n.archived ? (
                                <span
                                    data-testid={`${testidPrefix}-archived-pill-${n.id}`}
                                    className="text-[9px] uppercase tracking-[0.2em] text-amber-200 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                >
                                    Archived
                                </span>
                            ) : null}
                            {/* Iter-156B Phase 2 — visibility pill. Renders
                                only when scope is tighter than Project
                                Team so the default case stays visually
                                quiet. */}
                            <VisibilityPill
                                value={n.visibility}
                                testid={`${testidPrefix}-visibility-pill-${n.id}`}
                            />
                            {!n.scene_id && !n.character_id ? (
                                <span className="text-[9px] uppercase tracking-[0.2em] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-full px-1.5 py-0.5">
                                    Unmapped
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-0.5">
                        <FlagButton
                            projectId={projectId}
                            entityType={n.label === "script" ? "script_note" : (sceneId ? "scene_note" : (characterId ? "character_note" : "supervisor_note"))}
                            entityId={n.id}
                            entityLabel="note"
                            size="xs"
                            testid={`${testidPrefix}-flag-${n.id}`}
                        />
                        {(() => {
                            // Iter-156D — row-level Edit / Delete is
                            // creator-or-admin scoped. `isOwner` prop
                            // here is semantically "owner-or-admin
                            // governance override" (kept name for
                            // back-compat). Contributors see Edit /
                            // Delete only on notes they authored.
                            const canRowEdit = isOwner || (myId && n.created_by === myId);
                            return canRowEdit ? (
                            <>
                                <button
                                    type="button"
                                    data-testid={`${testidPrefix}-archive-${n.id}`}
                                    onClick={() => toggleArchive(n)}
                                    disabled={archivingId === n.id}
                                    aria-label={n.archived ? "Restore from archive" : "Archive note"}
                                    title={n.archived ? "Restore" : "Archive (non-destructive)"}
                                    className={`tap inline-flex items-center justify-center rounded-md h-7 w-7 ${
                                        n.archived
                                            ? "text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                                            : "text-neutral-400 hover:text-amber-300 hover:bg-amber-500/10"
                                    }`}
                                >
                                    {archivingId === n.id ? (
                                        <Loader2 size={12} className="animate-spin" />
                                    ) : n.archived ? (
                                        <ArchiveRestore size={12} />
                                    ) : (
                                        <Archive size={12} />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    data-testid={`${testidPrefix}-edit-${n.id}`}
                                    onClick={() => startEdit(n)}
                                    aria-label="Edit supervisor note"
                                    className="tap inline-flex items-center justify-center rounded-md h-7 w-7 text-neutral-400 hover:text-violet-200 hover:bg-violet-500/10"
                                >
                                    <Pencil size={12} />
                                </button>
                                <button
                                    type="button"
                                    data-testid={`${testidPrefix}-delete-${n.id}`}
                                    onClick={() => requestDelete(n.id)}
                                    disabled={deletingId === n.id}
                                    aria-label="Delete supervisor note"
                                    className="tap inline-flex items-center justify-center rounded-md h-7 w-7 text-neutral-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                    {deletingId === n.id ? (
                                        <Loader2 size={12} className="animate-spin" />
                                    ) : (
                                        <Trash2 size={12} />
                                    )}
                                </button>
                            </>
                            ) : null;
                        })()}
                    </div>
                </li>
            ))}
        </ul>
        {/* iter-73 PART 3 — destructive-action confirmation. */}
        <AlertDialog
            open={Boolean(confirmDeleteId)}
            onOpenChange={(o) => !o && setConfirmDeleteId(null)}
        >
            <AlertDialogContent data-testid={`${testidPrefix}-delete-confirm`}>
                <AlertDialogHeader>
                    <AlertDialogTitle>Delete this note?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This action cannot be undone.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel
                        data-testid={`${testidPrefix}-delete-confirm-cancel`}
                    >
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        data-testid={`${testidPrefix}-delete-confirm-delete`}
                        onClick={() =>
                            confirmDeleteId && onDelete(confirmDeleteId)
                        }
                        className="bg-red-600 text-white hover:bg-red-500"
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        <EditSupervisorNoteDialog
            open={Boolean(editingNote)}
            onOpenChange={(v) => !v && setEditingNote(null)}
            note={editingNote}
            scenes={editScenes}
            characters={editChars}
            onSaved={saveEdit}
        />
        </>
    );
}
